<template>
  <v-card class="ship mx-auto" max-width="600">
    <v-card-title>
      <router-link :to="{ name: 'Ships', params: { ship_id: ship.type_id } }">
        {{ ship.name }}
      </router-link>, {{ fitting.name }}
    </v-card-title>
    <v-card-text>
      <h2>EFT</h2>
      <pre>{{ fitting.eft }}</pre>
      <v-btn @click="copyToClipboard(fitting.eft)">Copy EFT to clipboard</v-btn>
    </v-card-text>
    <v-card-text>
      <h2>Requirements</h2>
      <v-simple-table class="sortable">
        <template v-slot:default>
          <thead>
            <tr>
              <th></th>
              <th class="text-left">Skill</th>
              <th class="text-left">Currently have skill</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="requirement in fitting.requirements" :key="requirement">
              <td class="handle">::</td>
              <td>{{ requirement }}</td>
              <td>{{ character_skills.includes(requirement) ? 'Yes' : 'No' }}</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-dialog v-model="delete_dialog" persistent max-width="290">
        <template v-slot:activator="{ on }">
          <v-btn color="red" v-on="on">Delete fitting</v-btn>
        </template>
        <v-card>
          <v-card-title class="headline">Delete fitting</v-card-title>
          <v-card-text>
            This will delete the fitting out of the game. Are you sure you want
            to do this?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="red darken-1" text @click="delete_dialog = false">
              No
            </v-btn>
            <v-btn
              color="red darken-1"
              text
              @click="deleteFitting(fitting.fitting_id)"
            >
              Yes
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import Sortable from "sortablejs";

function getTypeName(type_id, types) {
  return types[type_id] ? types[type_id].name : "unknown";
}

function filterItems(name, items) {
  return items
    .filter(x => x.flag.includes(name))
    .sort((a, b) => (a.flag > b.flag ? 1 : -1));
}

const required_skill_attribute_ids = [182, 183, 184, 1285, 1289, 1290];
const required_level_attribute_ids = [277, 278, 279, 1286, 1287, 1288];
const roman_numerals = ["", "I", "II", "III", "IV", "V"];
function getRequirements(
  dogma,
  parent_attributes,
  all_skills,
  requirements = []
) {
  const skill_level = parent_attributes.filter(y => {
    const index = required_skill_attribute_ids.indexOf(dogma.attribute_id);
    return y.attribute_id == required_level_attribute_ids[index];
  })[0].value;
  const active_skill = all_skills.find(x => x.type_id == dogma.value);
  for (let i = 1; i <= skill_level; i++) {
    const skill = `${active_skill.name} ${roman_numerals[i]}`;
    requirements.push(skill);
  }
  const requirement_attributes = (active_skill.dogma_attributes || [])
    .filter(x => required_skill_attribute_ids.includes(x.attribute_id))
    .sort(
      (a, b) =>
        required_skill_attribute_ids.indexOf(a.attribute_id) -
        required_skill_attribute_ids.indexOf(b.attribute_id)
    );
  if (requirement_attributes.length > 0) {
    for (let nested_dogma of requirement_attributes) {
      getRequirements(
        nested_dogma,
        active_skill.dogma_attributes,
        all_skills,
        requirements
      );
    }
  }
  return requirements;
}

export default {
  name: "FittingCard",
  props: {
    fitting_id: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      delete_dialog: false
    };
  },
  computed: {
    ...mapState({
      fitting(state) {
        const found_fit = state.fittings.find(
          x => x.fitting_id == this.fitting_id
        );
        if (!found_fit) {
          this.$router.push({ name: "Fittings", params: { ship_id: null } });
          return;
        }
        const fit = {
          ...found_fit,
          slots: {
            low: filterItems("LoSlot", found_fit.items),
            medium: filterItems("MedSlot", found_fit.items),
            high: filterItems("HiSlot", found_fit.items),
            rigs: filterItems("RigSlot", found_fit.items),
            subs: filterItems("SubSystemSlot", found_fit.items),
            drones: filterItems("DroneBay", found_fit.items),
            cargo: filterItems("Cargo", found_fit.items)
          }
        };
        const ship = state.ships.find(x => x.type_id == fit.ship_type_id)
        fit.eft = `[${ship.name}, ${fit.name}]\n`;
        const slots = [
          "low",
          "medium",
          "high",
          "rigs",
          "subs",
          "drones",
          "cargo"
        ];
        fit.eft += slots
          .map(slot => {
            if (fit.slots[slot].length == 0) return;
            return fit.slots[slot]
              .map(item => {
                let value = `${getTypeName(item.type_id, this.types)}`;
                if (["drones", "cargo"].includes(slot)) {
                  value += ` x${item.quantity}`;
                }
                return value;
              })
              .join("\n");
          })
          .join("\n\n");
        const mod_reqs = Object.values(fit.slots).flat().map(slot_item => {
          const item = state.types.find(x => x.type_id == slot_item.type_id);
          return item.dogma_attributes
            .filter(x => required_skill_attribute_ids.includes(x.attribute_id))
            .map(attr =>
              getRequirements(attr, item.dogma_attributes, state.skills)
            )
            .flat();
        })
          .filter(Boolean)
          .flat();
        const ship_reqs = ship.dogma_attributes
          .filter(x => required_skill_attribute_ids.includes(x.attribute_id))
            .map(attr =>
              getRequirements(attr, ship.dogma_attributes, state.skills)
            )
            .flat();
        fit.requirements = new Set([...mod_reqs, ...ship_reqs].sort());
        return fit;
      },
      ship(state) {
        return state.ships.find(x => x.type_id == this.fitting.ship_type_id);
      },
      types: state => Object.fromEntries(state.types.map(x => [x.type_id, x])),
      skills: "skills",
      character_skills: state => {
        return Object.values(state.character_skills).flat().map(x => {
          if (x.level == 0) return;
          const result = [];
          for(let i = 1; i <= x.level; i++) {
            result.push(`${x.skill.name} ${roman_numerals[i]}`);
          }
          return result;
        }).filter(Boolean).flat();
      }
    })
  },
  methods: {
    copyToClipboard(text) {
      navigator.permissions.query({ name: "clipboard-write" }).then(result => {
        if (result.state == "granted" || result.state == "prompt") {
          navigator.clipboard.writeText(text);
        }
      });
    },
    deleteFitting(fitting_id) {
      this.delete_dialog = false;
      this.$store.dispatch("deleteFitting", fitting_id);
    }
  },
  mounted() {
    let table = document.querySelector(".v-data-table.sortable tbody");
    // const _self = this;
    Sortable.create(table, {
      handle: ".handle"
      // onEnd({ newIndex, oldIndex }) {
        // const rowSelected = _self.fitting.requirements.splice(oldIndex, 1)[0];
        // _self.fitting.requirements.splice(newIndex, 0, rowSelected);
      // }
    });
  }
};
</script>

<style lang="scss" scoped>
.handle {
  cursor: ns-resize;
}
</style>
